import React, { useEffect, useState } from 'react';
import { usePerformanceMonitoring } from '../hooks/usePerformanceMonitoring';
import { useConnectionStatus } from '../hooks/useConnectionStatus';
import { 
  Cpu, 
  Wifi, 
  WifiOff, 
  AlertCircle, 
  Clock, 
  Database, 
  BarChart3, 
  Activity, 
  Download, 
  FileText, 
  Layers, 
  Zap
} from 'lucide-react';

interface PerformanceMonitorProps {
  visible?: boolean;
}

export const PerformanceMonitor: React.FC<PerformanceMonitorProps> = ({ visible = false }) => {
  const { metrics: webVitals } = usePerformanceMonitoring();
  const { status: connectionStatus, lastError, retryConnection, isRetrying } = useConnectionStatus();
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<'vitals' | 'components' | 'api' | 'memory' | 'errors'>('vitals');
  const [renderCount, setRenderCount] = useState(0);
  const [lastRenderTime, setLastRenderTime] = useState(0);
  const [performanceSummary, setPerformanceSummary] = useState({
    slowestComponents: [] as {name: string, avgTime: number}[],
    slowestApiCalls: [] as {name: string, avgTime: number}[],
    memoryTrend: 'stable' as 'increasing' | 'stable' | 'decreasing',
    errorCount: 0,
    longTaskCount: 0
  });
  const [collectedMetrics, setCollectedMetrics] = useState({
    componentRenderTimes: {} as Record<string, number[]>,
    apiCallTimes: {} as Record<string, number[]>,
    longTasks: [] as { name: string; duration: number; timestamp: number }[],
    errors: [] as { message: string; location: string; timestamp: number }[],
    resourceLoadTimes: [] as { name: string; duration: number; timestamp: number }[],
    memorySnapshots: [] as { usedJSHeapSize: number; totalJSHeapSize: number; timestamp: number }[]
  });

  // Track render performance
  useEffect(() => {
    const startTime = performance.now();
    
    return () => {
      const endTime = performance.now();
      const renderTime = endTime - startTime;
      setLastRenderTime(renderTime);
      setRenderCount(prev => prev + 1);
    };
  });

  // Update performance summary periodically
  useEffect(() => {
    const updateInterval = setInterval(() => {
      setPerformanceSummary({
        slowestComponents: [],
        slowestApiCalls: [],
        memoryTrend: 'stable',
        errorCount: collectedMetrics.errors.length,
        longTaskCount: collectedMetrics.longTasks.length
      });
    }, 5000);
    
    return () => clearInterval(updateInterval);
  }, [collectedMetrics]);

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <div 
        className={`bg-gray-800 border border-gray-700 rounded-lg shadow-lg transition-all duration-300 ${
          isExpanded ? 'w-96' : 'w-auto'
        }`}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between p-3 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center space-x-3">
            <Activity className="h-4 w-4 text-blue-400" />
            <div>
              <span className="text-sm font-medium text-white">Performance Monitor</span>
              <div className="flex items-center space-x-1 mt-0.5">
                <div className={`w-2 h-2 rounded-full ${
                  performanceSummary.memoryTrend === 'increasing' ? 'bg-red-400' : 
                  performanceSummary.memoryTrend === 'decreasing' ? 'bg-green-400' : 
                  'bg-blue-400'
                } animate-pulse`}></div>
                <span className="text-xs text-gray-400">
                  {performanceSummary.errorCount > 0 ? `${performanceSummary.errorCount} errors` : 'No errors'}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {connectionStatus === 'connected' ? (
              <Wifi className="h-4 w-4 text-green-400" />
            ) : connectionStatus === 'connecting' ? (
              <Wifi className="h-4 w-4 text-yellow-400 animate-pulse" />
            ) : (
              <WifiOff className="h-4 w-4 text-red-400" />
            )}
          </div>
        </div>
        
        {/* Expanded Content */}
        {isExpanded && (
          <div className="border-t border-gray-700">
            {/* Tabs */}
            <div className="flex border-b border-gray-700">
              <button
                onClick={() => setActiveTab('vitals')}
                className={`px-3 py-2 text-xs ${
                  activeTab === 'vitals' 
                    ? 'text-blue-400 border-b-2 border-blue-400' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Web Vitals
              </button>
              <button
                onClick={() => setActiveTab('components')}
                className={`px-3 py-2 text-xs ${
                  activeTab === 'components' 
                    ? 'text-blue-400 border-b-2 border-blue-400' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Components
              </button>
              <button
                onClick={() => setActiveTab('api')}
                className={`px-3 py-2 text-xs ${
                  activeTab === 'api' 
                    ? 'text-blue-400 border-b-2 border-blue-400' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                API
              </button>
              <button
                onClick={() => setActiveTab('memory')}
                className={`px-3 py-2 text-xs ${
                  activeTab === 'memory' 
                    ? 'text-blue-400 border-b-2 border-blue-400' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Memory
              </button>
              <button
                onClick={() => setActiveTab('errors')}
                className={`px-3 py-2 text-xs ${
                  activeTab === 'errors' 
                    ? 'text-red-400 border-b-2 border-red-400' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Errors {performanceSummary.errorCount > 0 && `(${performanceSummary.errorCount})`}
              </button>
            </div>
            
            {/* Tab Content */}
            <div className="p-3">
              {activeTab === 'vitals' && (
                <div className="space-y-3">
                  {/* Web Vitals */}
                  <div>
                    <h4 className="text-xs font-medium text-gray-400 mb-2 flex items-center">
                      <Clock className="h-3 w-3 mr-1" />
                      Web Vitals
                    </h4>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-400">FCP:</span>
                        <span className={`font-medium ${getMetricColor(webVitals.fcp, 1800, 3000)}`}>
                          {webVitals.fcp ? `${webVitals.fcp.toFixed(0)}ms` : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">LCP:</span>
                        <span className={`font-medium ${getMetricColor(webVitals.lcp, 2500, 4000)}`}>
                          {webVitals.lcp ? `${webVitals.lcp.toFixed(0)}ms` : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">FID:</span>
                        <span className={`font-medium ${getMetricColor(webVitals.fid, 100, 300)}`}>
                          {webVitals.fid ? `${webVitals.fid.toFixed(0)}ms` : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">CLS:</span>
                        <span className={`font-medium ${getMetricColor(webVitals.cls, 0.1, 0.25, true)}`}>
                          {webVitals.cls ? webVitals.cls.toFixed(3) : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">TTFB:</span>
                        <span className={`font-medium ${getMetricColor(webVitals.ttfb, 800, 1800)}`}>
                          {webVitals.ttfb ? `${webVitals.ttfb.toFixed(0)}ms` : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Connection Status */}
                  <div>
                    <h4 className="text-xs font-medium text-gray-400 mb-2 flex items-center">
                      <Database className="h-3 w-3 mr-1" />
                      Connection Status
                    </h4>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">Network:</span>
                      <span className={`font-medium ${webVitals.isOnline ? 'text-green-400' : 'text-red-400'}`}>
                        {webVitals.isOnline ? 'Online' : 'Offline'}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">Supabase:</span>
                      <span className={`font-medium ${
                        connectionStatus === 'connected' 
                          ? 'text-green-400' 
                          : connectionStatus === 'connecting' 
                          ? 'text-yellow-400' 
                          : 'text-red-400'
                      }`}>
                        {connectionStatus === 'connected' 
                          ? 'Connected' 
                          : connectionStatus === 'connecting' 
                          ? 'Connecting...' 
                          : connectionStatus === 'timeout'
                          ? 'Timeout'
                          : 'Disconnected'}
                      </span>
                    </div>
                    {lastError && (
                      <div className="mt-1 p-1 bg-red-900/20 rounded text-xs text-red-400 flex items-start">
                        <AlertCircle className="h-3 w-3 mr-1 flex-shrink-0 mt-0.5" />
                        <span className="line-clamp-2">{lastError}</span>
                      </div>
                    )}
                    {connectionStatus !== 'connected' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          retryConnection();
                        }}
                        disabled={isRetrying}
                        className="mt-2 w-full px-2 py-1 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 text-white rounded text-xs font-medium transition-colors"
                      >
                        {isRetrying ? 'Retrying...' : 'Retry Connection'}
                      </button>
                    )}
                  </div>
                  
                  {/* Render Performance */}
                  <div>
                    <h4 className="text-xs font-medium text-gray-400 mb-2 flex items-center">
                      <BarChart3 className="h-3 w-3 mr-1" />
                      Render Performance
                    </h4>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Render Count:</span>
                        <span className="text-white">{renderCount}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Last Render:</span>
                        <span className={`font-medium ${
                          lastRenderTime > 50 ? 'text-red-400' : 
                          lastRenderTime > 16 ? 'text-yellow-400' : 'text-green-400'
                        }`}>
                          {lastRenderTime.toFixed(1)}ms
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Long Tasks:</span>
                        <span className="text-white">{performanceSummary.longTaskCount}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {activeTab === 'components' && (
                <div className="space-y-3">
                  <h4 className="text-xs font-medium text-gray-400 mb-2 flex items-center">
                    <Layers className="h-3 w-3 mr-1" />
                    Component Render Times
                  </h4>
                  
                  {performanceSummary.slowestComponents.length > 0 ? (
                    <div className="space-y-2">
                      {performanceSummary.slowestComponents.map((component, index) => (
                        <div key={index} className="flex justify-between items-center text-xs">
                          <span className="text-gray-300 truncate max-w-[70%]">{component.name}</span>
                          <span className={`${
                            component.avgTime > 100 ? 'text-red-400' : 
                            component.avgTime > 50 ? 'text-yellow-400' : 'text-green-400'
                          }`}>
                            {component.avgTime.toFixed(1)}ms
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">No component render data available</p>
                  )}
                  
                  <div className="mt-2 pt-2 border-t border-gray-700">
                    <h4 className="text-xs font-medium text-gray-400 mb-2">Component Render Count</h4>
                    <div className="space-y-2">
                      {Object.entries(collectedMetrics.componentRenderTimes).slice(0, 5).map(([name, times], index) => (
                        <div key={index} className="flex justify-between items-center text-xs">
                          <span className="text-gray-300 truncate max-w-[70%]">{name}</span>
                          <span className="text-white">{times.length} renders</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {activeTab === 'api' && (
                <div className="space-y-3">
                  <h4 className="text-xs font-medium text-gray-400 mb-2 flex items-center">
                    <Zap className="h-3 w-3 mr-1" />
                    API Call Times
                  </h4>
                  
                  {performanceSummary.slowestApiCalls.length > 0 ? (
                    <div className="space-y-2">
                      {performanceSummary.slowestApiCalls.map((api, index) => (
                        <div key={index} className="flex justify-between items-center text-xs">
                          <span className="text-gray-300 truncate max-w-[70%]">{api.name}</span>
                          <span className={`${
                            api.avgTime > 1000 ? 'text-red-400' : 
                            api.avgTime > 500 ? 'text-yellow-400' : 'text-green-400'
                          }`}>
                            {api.avgTime.toFixed(1)}ms
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">No API call data available</p>
                  )}
                  
                  <div className="mt-2 pt-2 border-t border-gray-700">
                    <h4 className="text-xs font-medium text-gray-400 mb-2">Resource Load Times</h4>
                    <div className="space-y-2">
                      {collectedMetrics.resourceLoadTimes.slice(-5).map((resource, index) => (
                        <div key={index} className="flex justify-between items-center text-xs">
                          <span className="text-gray-300 truncate max-w-[70%]">
                            {resource.name.split('/').pop() || resource.name}
                          </span>
                          <span className={`${
                            resource.duration > 1000 ? 'text-red-400' : 
                            resource.duration > 500 ? 'text-yellow-400' : 'text-green-400'
                          }`}>
                            {resource.duration.toFixed(1)}ms
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {activeTab === 'memory' && (
                <div className="space-y-3">
                  <h4 className="text-xs font-medium text-gray-400 mb-2 flex items-center">
                    <Cpu className="h-3 w-3 mr-1" />
                    Memory Usage
                  </h4>
                  
                  {webVitals.memory ? (
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-400">Used Heap:</span>
                        <span className="text-white">
                          {(webVitals.memory.usedJSHeapSize / 1048576).toFixed(1)} MB
                        </span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-1.5">
                        <div 
                          className={`h-full rounded-full ${
                            webVitals.memory.usedJSHeapSize / webVitals.memory.jsHeapSizeLimit > 0.8
                              ? 'bg-red-500'
                              : webVitals.memory.usedJSHeapSize / webVitals.memory.jsHeapSizeLimit > 0.5
                              ? 'bg-yellow-500'
                              : 'bg-green-500'
                          }`}
                          style={{ 
                            width: `${(webVitals.memory.usedJSHeapSize / webVitals.memory.jsHeapSizeLimit) * 100}%` 
                          }}
                        ></div>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>0</span>
                        <span>{(webVitals.memory.jsHeapSizeLimit / 1048576).toFixed(0)} MB</span>
                      </div>
                      
                      <div className="mt-2 pt-2 border-t border-gray-700">
                        <h5 className="text-xs font-medium text-gray-400 mb-2">Memory Trend</h5>
                        <div className="flex items-center space-x-2">
                          <div className={`w-2 h-2 rounded-full ${
                            performanceSummary.memoryTrend === 'increasing' ? 'bg-red-400' : 
                            performanceSummary.memoryTrend === 'decreasing' ? 'bg-green-400' : 
                            'bg-blue-400'
                          }`}></div>
                          <span className="text-xs text-gray-300">
                            {performanceSummary.memoryTrend === 'increasing' ? 'Increasing (potential leak)' : 
                             performanceSummary.memoryTrend === 'decreasing' ? 'Decreasing (good)' : 
                             'Stable'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">Memory metrics not available</p>
                  )}
                  
                  {collectedMetrics.memorySnapshots.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-700">
                      <h5 className="text-xs font-medium text-gray-400 mb-2">Memory History</h5>
                      <div className="space-y-2">
                        {collectedMetrics.memorySnapshots.slice(-5).map((snapshot, index) => (
                          <div key={index} className="flex justify-between items-center text-xs">
                            <span className="text-gray-400">
                              {new Date(snapshot.timestamp).toLocaleTimeString()}:
                            </span>
                            <span className="text-white">
                              {(snapshot.usedJSHeapSize / 1048576).toFixed(1)} MB
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {activeTab === 'errors' && (
                <div className="space-y-3">
                  <h4 className="text-xs font-medium text-gray-400 mb-2 flex items-center">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    Errors & Warnings
                  </h4>
                  
                  {collectedMetrics.errors.length > 0 ? (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {collectedMetrics.errors.slice().reverse().map((error, index) => (
                        <div key={index} className="p-1.5 bg-red-900/20 rounded text-xs text-red-400">
                          <div className="flex justify-between mb-1">
                            <span className="font-medium">{error.location.split('/').pop() || error.location}</span>
                            <span className="text-gray-400 text-xs">
                              {new Date(error.timestamp).toLocaleTimeString()}
                            </span>
                          </div>
                          <p className="line-clamp-2">{error.message}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">No errors recorded</p>
                  )}
                  
                  <div className="mt-2 pt-2 border-t border-gray-700">
                    <h5 className="text-xs font-medium text-gray-400 mb-2">Long Tasks</h5>
                    {collectedMetrics.longTasks.length > 0 ? (
                      <div className="space-y-2 max-h-20 overflow-y-auto">
                        {collectedMetrics.longTasks.slice().reverse().slice(0, 3).map((task, index) => (
                          <div key={index} className="flex justify-between items-center text-xs">
                            <span className="text-gray-300">{task.name}</span>
                            <span className={`${
                              task.duration > 100 ? 'text-red-400' : 'text-yellow-400'
                            }`}>
                              {task.duration.toFixed(1)}ms
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500">No long tasks recorded</p>
                    )}
                  </div>
                  
                  <div className="flex justify-end mt-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // Export performance data
                        const data = JSON.stringify(collectedMetrics, null, 2);
                        const blob = new Blob([data], { type: 'application/json' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `performance-report-${new Date().toISOString().split('T')[0]}.json`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                      }}
                      className="flex items-center space-x-1 px-2 py-1 bg-blue-600 hover:bg-blue-500 text-white rounded text-xs font-medium transition-colors"
                    >
                      <Download className="h-3 w-3" />
                      <span>Export Report</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            {/* Footer with quick stats */}
            <div className="p-2 border-t border-gray-700 flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${
                  webVitals.memory && webVitals.memory.usedJSHeapSize / webVitals.memory.jsHeapSizeLimit > 0.8
                    ? 'bg-red-400'
                    : webVitals.memory && webVitals.memory.usedJSHeapSize / webVitals.memory.jsHeapSizeLimit > 0.5
                    ? 'bg-yellow-400'
                    : 'bg-green-400'
                } animate-pulse`}></div>
                <span className="text-xs text-gray-400">
                  {webVitals.memory ? `${(webVitals.memory.usedJSHeapSize / 1048576).toFixed(1)}MB` : 'N/A'}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-400">
                  {webVitals.fcp ? `FCP: ${webVitals.fcp.toFixed(0)}ms` : 'FCP: N/A'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper function to determine color based on metric value
const getMetricColor = (value: number | undefined, warning: number, critical: number, isInverted = false): string => {
  if (value === undefined) return 'text-gray-400';
  
  if (isInverted) {
    if (value < warning) return 'text-green-400';
    if (value < critical) return 'text-yellow-400';
    return 'text-red-400';
  } else {
    if (value < warning) return 'text-green-400';
    if (value < critical) return 'text-yellow-400';
    return 'text-red-400';
  }
};